import type { ColumnDef } from '@tanstack/react-table';
import { DifficultyCell } from './DifficultyCell';
import { DurationCell, formatDuration } from './DurationCell';
import { getLocalizedSongTitle } from './songDisplay';
import { SongArtworkCell } from './SongArtworkCell';
import { SongTitleCell } from './SongTitleCell';
import { SortableHeader } from './SortableHeader';
import type { DifficultyConfig, Song } from './types';
import type { Locale } from '~/i18n';
import { Table } from '~/components/ui/table';

type Props = {
  difficultyConfigs: DifficultyConfig[];
  locale: Locale;
  t: (key: string) => string;
};

function getLocalizedUnitName(song: Song, locale: Locale) {
  return locale.startsWith('en') && song.unitEnglishName ? song.unitEnglishName : song.unitName;
}

export function getSongTableColumns({ difficultyConfigs, locale, t }: Props): ColumnDef<Song>[] {
  return [
    {
      accessorKey: 'id',
      header: (context) => <SortableHeader {...context} hideBelow="md" />,
      cell: ({ row }) => <SongArtworkCell song={row.original} />,
      enableSorting: false
    },
    {
      accessorFn: (row) => getLocalizedSongTitle(row, locale),
      id: 'title',
      header: (context) => <SortableHeader {...context}>{t('song_table.name')}</SortableHeader>,
      cell: ({ row }) => (
        <SongTitleCell
          song={row.original}
          title={getLocalizedSongTitle(row.original, locale)}
          secondaryTitle={
            locale.startsWith('en') &&
            row.original.englishName &&
            row.original.englishName !== row.original.title
              ? row.original.title
              : undefined
          }
          mobileMeta={{
            description: getLocalizedUnitName(row.original, locale) ?? row.original.description,
            duration: formatDuration(row.original.duration)
          }}
        />
      ),
      sortingFn: 'alphanumeric'
    },
    {
      accessorFn: (row) => getLocalizedUnitName(row, locale) ?? row.description,
      id: 'unit',
      header: (context) => (
        <SortableHeader {...context} hideBelow="md">
          {t('song_table.description')}
        </SortableHeader>
      ),
      cell: ({ getValue }) => <Table.Cell hideBelow="md">{getValue() as string}</Table.Cell>,
      sortingFn: 'alphanumeric'
    },
    {
      accessorKey: 'duration',
      header: (context) => (
        <SortableHeader {...context} hideBelow="md" textAlign="center">
          {t('song_table.length')}
        </SortableHeader>
      ),
      cell: ({ getValue }) => <DurationCell duration={getValue() as number} />,
      sortingFn: 'datetime'
    },
    ...difficultyConfigs.map(
      (config): ColumnDef<Song> => ({
        accessorFn: (row) => row.difficulty[config.key],
        id: config.key,
        header: (context) => (
          <SortableHeader {...context} minW="16" p={{ base: 1, md: 4 }} textAlign="center">
            {config.label}
          </SortableHeader>
        ),
        sortingFn: 'alphanumeric',
        cell: ({ row, getValue }) => (
          <DifficultyCell config={config} song={row.original} value={getValue() as number} />
        )
      })
    )
  ];
}
